import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
// import { authHeaders } from "../../../utils/auth";
// import { authorizedFetch } from "../../../utils/auth";
import { apiFetchJson, appFetchJson } from "../../../utils/http";

// BASE: deriva de /list
const CLIENTES_BASE = (apiRoutes?.clientes?.list || "").replace(
  /\/list(?:\?.*)?$/,
  "",
);

// Endpoints
const INIT_URL = (id) => `${CLIENTES_BASE}/${encodeURIComponent(id)}/editar`; // GET: datos + combos
const UPDATE_URL = (id) => `${CLIENTES_BASE}/${encodeURIComponent(id)}/editar`; // PATCH
const UPDATE_METHOD = "PATCH";

// helper email simple
const isEmail = (s) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || "").trim());

export default function EditarCliente({ id, codigo, onClose, onSaved }) {
  const pk = id ?? codigo;

  // init = { cliente, estadosCliente, ciudades, monedas, tiposDocumento, alertasEmail }
  const [init, setInit] = useState(null);
  const cliente = init?.cliente;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // EDITABLES
  const [textoObservaciones, setTextoObservaciones] = useState("");
  const [clieCdAlertaE, setClieCdAlertaE] = useState("NO"); // "SI" | "NO"
  const [email, setEmail] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState(""); // "RUC" | "CEDULA" | "PASAPORTE" | ""
  const [estadoId, setEstadoId] = useState(""); // id string
  const [ciudadId, setCiudadId] = useState(""); // id string
  const [monedaId, setMonedaId] = useState(""); // id string

  const V = (v) => (v == null ? "" : v);
  const D = (v) => (v == null || v === "" ? "-" : v);

  // 👇 Evitar doble-carga en dev (StrictMode): salteamos la PRIMERA ejecución
  const devRunRef = useRef(false);

  // carga init (detalle + combos)
  useEffect(() => {
    let alive = true;

    if (pk == null || pk === "") {
      setLoading(false);
      return;
    }

    if (process.env.NODE_ENV !== "production") {
      if (!devRunRef.current) {
        devRunRef.current = true; // primera pasada en dev → no ejecutar aún
        return;
      }
    }

    (async () => {
      try {
        setLoading(true);
        const url = INIT_URL(pk);
        // const resp = await fetch(url, {
        //               headers: authHeaders()
        //             });
        // if (!resp.ok) {
        //   const t = await resp.text();
        //   throw new Error(`GET ${url} -> ${resp.status} ${resp.statusText}. Body: ${t}`);
        // }
        // const data = await resp.json();
        const data = await apiFetchJson(url); // ✅ wrapper (401/403 + JSON)
        if (!alive) return;

        setInit(data);

        // mapear campos editables desde data.cliente
        const c = data?.cliente || {};
        setTextoObservaciones(V(c.textoObservaciones));
        setClieCdAlertaE(V(c.clieCdAlertaE) || "NO");
        setEmail(V(c.dataEmail));
        setTipoDocumento(V(c.codigoTipodocumento) || "");
        setEstadoId(c?.estadoId != null ? String(c.estadoId) : "");
        setCiudadId(c?.ciudadId != null ? String(c.ciudadId) : "");
        setMonedaId(c?.monedaId != null ? String(c.monedaId) : "");
      } catch (e) {
        console.error("Error cargando edición:", e);
        alert("No se pudo cargar el cliente/combos. Revisá la consola.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [pk, id, codigo]);

  // cambios
  const hasChanges = useMemo(() => {
    if (!cliente) return false;

    const sameObs = V(cliente.textoObservaciones) === V(textoObservaciones);
    const sameAlert = V(cliente.clieCdAlertaE || "NO") === V(clieCdAlertaE);
    const sameEmail = V(cliente.dataEmail) === V(email);
    const sameTipoDoc =
      V(cliente.codigoTipodocumento || "") === V(tipoDocumento || "");
    const sameEstado =
      String(cliente?.estadoId ?? "") === String(estadoId ?? "");
    const sameCiudad =
      String(cliente?.ciudadId ?? "") === String(ciudadId ?? "");
    const sameMoneda =
      String(cliente?.monedaId ?? "") === String(monedaId ?? "");

    return !(
      sameObs &&
      sameAlert &&
      sameEmail &&
      sameTipoDoc &&
      sameEstado &&
      sameCiudad &&
      sameMoneda
    );
  }, [
    cliente,
    textoObservaciones,
    clieCdAlertaE,
    email,
    tipoDocumento,
    estadoId,
    ciudadId,
    monedaId,
  ]);

  // validación: si alerta = SI, debe haber email válido
  const alertaSiRequiereEmail =
    clieCdAlertaE?.toUpperCase() === "SI" && !isEmail(email);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!cliente) return;

    if (alertaSiRequiereEmail) {
      alert("Para habilitar alerta por email (SI), ingresá un email válido.");
      return;
    }

    // construir payload solo con cambios
    const body = {};

    if (V(cliente.textoObservaciones) !== V(textoObservaciones)) {
      body.textoObservaciones = V(textoObservaciones);
    }
    if (V(cliente.clieCdAlertaE || "NO") !== V(clieCdAlertaE)) {
      body.alertaEmail = String(clieCdAlertaE || "")
        .trim()
        .toUpperCase();
    }
    if (V(cliente.dataEmail) !== V(email)) {
      body.email = V(email);
    }
    if (V(cliente.codigoTipodocumento || "") !== V(tipoDocumento || "")) {
      body.tipoDocumento = String(tipoDocumento || "")
        .trim()
        .toUpperCase();
    }

    const toNum = (s) => (s === "" || s == null ? null : Number(s));
    const estNum = toNum(estadoId);
    const origEst = cliente?.estadoId != null ? Number(cliente.estadoId) : null;
    if ((estNum ?? null) !== (origEst ?? null)) {
      if (estNum != null && !Number.isNaN(estNum)) body.estadoId = estNum;
    }

    const ciuNum = toNum(ciudadId);
    const origCiu = cliente?.ciudadId != null ? Number(cliente.ciudadId) : null;
    if ((ciuNum ?? null) !== (origCiu ?? null)) {
      if (ciuNum != null && !Number.isNaN(ciuNum)) body.ciudadId = ciuNum;
    }

    const monNum = toNum(monedaId);
    const origMon = cliente?.monedaId != null ? Number(cliente.monedaId) : null;
    if ((monNum ?? null) !== (origMon ?? null)) {
      if (monNum != null && !Number.isNaN(monNum)) body.monedaId = monNum;
    }

    if (Object.keys(body).length === 0) return;

    setSaving(true);
    try {
      const url = UPDATE_URL(cliente.id);
      // const resp = await authorizedFetch(url, {
      //   method: UPDATE_METHOD,
      //   headers: { "Content-Type": "application/json; charset=UTF-8" },
      //   body: JSON.stringify(body),
      // });
      // if (!resp.ok) {
      //   const t = await resp.text();
      //   throw new Error(`UPDATE ${url} -> ${resp.status} ${resp.statusText}. Body: ${t}`);
      // }
      await appFetchJson(url, {
        method: UPDATE_METHOD,
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify(body),
      }); // ✅ wrapper (401/403 + JSON o texto, lanza si !ok)

      onSaved?.({
        ...cliente,
        textoObservaciones,
        clieCdAlertaE,
        dataEmail: email,
        codigoTipodocumento: tipoDocumento,
        // ⛔️ Mantengo tu lógica original sin tocar, corrigiendo las asignaciones:
        estadoId: estNum ?? cliente?.estadoId,
        ciudadId: ciuNum ?? cliente?.ciudadId,
        monedaId: monNum ?? cliente?.monedaId,
      });
      onClose?.();
    } catch (err) {
      console.error("Error al guardar edición:", err);
      alert("No se pudo guardar. Revisá la consola.");
    } finally {
      setSaving(false);
    }
  };

  // Helpers para combos
  const optDocs = init?.tiposDocumento || [];
  const optEstados = init?.estadosCliente || [];
  const optCiud = init?.ciudades || [];
  const optMon = init?.monedas || [];
  const optAlertas = init?.alertasEmail || [];

  return (
    <Modal title="Introduzca los datos de cliente" onClose={onClose}>
      {pk == null || pk === "" ? (
        <div className="py-12 text-center text-red-600">
          No recibí un identificador (id/código) para cargar el cliente.
        </div>
      ) : loading ? (
        <div className="loading-text py-12">Cargando...</div>
      ) : !cliente ? (
        <div className="py-12 text-center text-red-600">
          No se encontraron datos para el identificador proporcionado.
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          {/* SOLO LECTURA — solo campos NO editables */}
          <div className="space-y-2">
            {[
              ["ID", cliente?.id],
              ["Código", cliente?.codigo],
              ["Nombre", cliente?.nombre],
              ["Documento", cliente?.documento],
              ["Teléfono1", cliente?.dataTelefono1],
              ["Teléfono2", cliente?.dataTelefono2],
              ["Fax", cliente?.dataFax],
              ["Crédito Máximo", cliente?.valorCreditomaximo],
              ["Dirección", cliente?.dataDireccion],
              ["Cuenta Padre", cliente?.codigoCuentaPadre],
            ]
              .filter(([_, v]) => v != null && v !== "")
              .map(([label, val]) => (
                <Fila key={label} label={label} value={D(val)} />
              ))}
          </div>

          <div className="form-divider" />

          {/* EDITABLES */}
          <Campo label="Observaciones">
            <input
              value={textoObservaciones}
              onChange={(e) => setTextoObservaciones(e.target.value)}
              className="input"
              placeholder="Escriba la observación…"
            />
          </Campo>

          <Campo label="Email">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="usuario@dominio.com"
              type="email"
            />
          </Campo>

          <Campo label="Alertas Email">
            <div className="flex items-center gap-2">
              <select
                value={clieCdAlertaE}
                onChange={(e) => setClieCdAlertaE(e.target.value)}
                className="input"
              >
                {optAlertas.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.nombre}
                  </option>
                ))}
              </select>
              {clieCdAlertaE?.toUpperCase() === "SI" && !isEmail(email) && (
                <span className="text-sm text-red-600">
                  Ingresá un email válido para habilitar alertas.
                </span>
              )}
            </div>
          </Campo>

          <Campo label="Tipo de Documento">
            <select
              value={tipoDocumento}
              onChange={(e) => setTipoDocumento(e.target.value)}
              className="input"
            >
              <option value="">— Seleccionar —</option>
              {optDocs.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Estado">
            <select
              value={estadoId}
              onChange={(e) => setEstadoId(e.target.value)}
              className="input"
            >
              <option value="">— Seleccionar —</option>
              {optEstados.map((e) => (
                <option key={e.id} value={String(e.id)}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Ciudad">
            <select
              value={ciudadId}
              onChange={(e) => setCiudadId(e.target.value)}
              className="input"
            >
              <option value="">— Seleccionar —</option>
              {optCiud.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Moneda">
            <select
              value={monedaId}
              onChange={(e) => setMonedaId(e.target.value)}
              className="input"
            >
              <option value="">— Seleccionar —</option>
              {optMon.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <div className="inline-flex items-center gap-2">
            <button
              type="submit"
              disabled={saving || !hasChanges || alertaSiRequiereEmail}
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>

            <button type="button" onClick={onClose} className="btn-fil">
              Consultar
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/* ------- UI helpers ------- */
function Campo({ label, children }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}
function Fila({ label, value }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1">
        <div className="value">{value}</div>
      </div>
    </div>
  );
}
function Modal({ title, children, onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-panel-md">
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">{title}</h2>
          {/* <button onClick={onClose} className="modal-close-x">✕</button> */}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
